import { cn } from "@/lib/utils";
import { ui } from "@patient-watch/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export interface AppHubCardLink {
    to: string;
    label: string;
}

export interface AppHubCardProps {
    title: string;
    description: string;
    icon: ReactNode;
    iconBg: string;
    bgGradient: string;
    hoverOverlay: string;
    borderColor: string;
    primaryButtonClassName: string;
    links?: AppHubCardLink[];
    footer?: ReactNode;
}

export function AppHubCard({
    title,
    description,
    icon,
    iconBg,
    bgGradient,
    hoverOverlay,
    borderColor,
    primaryButtonClassName,
    links = [],
    footer,
}: AppHubCardProps) {
    return (
        <ui.Card
            className={cn(
                "group before:absolute relative before:inset-0 flex flex-col before:opacity-0 hover:before:opacity-100 shadow-sm hover:shadow-md border before:rounded-lg min-h-[320px] lg:min-h-[500px] overflow-hidden transition-all before:transition-opacity duration-300 before:duration-300 ease-in-out",
                bgGradient,
                hoverOverlay,
                borderColor,
                "dark:bg-slate-900/80 dark:bg-none dark:border-slate-700 dark:hover:border-slate-600 dark:text-card-foreground",
                "dark:before:bg-none dark:before:bg-white/5"
            )}
        >
            <ui.CardHeader className="z-10 relative flex flex-col items-start gap-4">
                <span
                    className={cn(
                        "flex justify-center items-center shadow-sm p-2 rounded-lg w-14 h-14 aspect-square",
                        iconBg
                    )}
                >
                    {icon}
                </span>
                <div>
                    <ui.CardTitle className="mb-2 text-card-foreground text-2xl">{title}</ui.CardTitle>
                    <ui.CardDescription className="text-base text-muted-foreground">{description}</ui.CardDescription>
                </div>
            </ui.CardHeader>
            <ui.CardContent className="flex-1" />
            <ui.CardFooter className="z-10 relative flex flex-col gap-2">
                {footer ??
                    links.map((link, index) => (
                        <ui.Button
                            key={`${link.to}-${link.label}`}
                            asChild
                            size="md"
                            variant={index === 0 ? "default" : "outline"}
                            className={cn("w-full", index === 0 && primaryButtonClassName)}
                        >
                            <Link to={link.to}>{link.label}</Link>
                        </ui.Button>
                    ))}
            </ui.CardFooter>
        </ui.Card>
    );
}
